import type { ReactNode } from "react";
import { BrandLogo, Login, type AuthCardProps } from "@dearbill/shared";

const STEPS = [
  {
    title: "Understand the options",
    description: "Review prescription payment estimates and find a useful next step.",
  },
  {
    title: "Refer with permission",
    description: "Connect a patient with DearBill for a review of support options.",
  },
  {
    title: "Follow their progress",
    description: "Keep track of referrals and reach our team when you need help.",
  },
];

function PartnerLoginLayout({ title, children, footer }: AuthCardProps): ReactNode {
  return (
    <div className="text-midnight min-h-screen bg-white">
      <a
        href="#partner-sign-in"
        className="focus:text-ember sr-only focus:not-sr-only focus:absolute focus:z-10 focus:rounded-lg focus:bg-white focus:p-4"
      >
        Skip to sign in
      </a>
      <header className="border-haze border-b">
        <div className="max-w-page mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-5 sm:px-8">
          <a href="https://dearbill.com/" aria-label="DearBill home" className="py-2">
            <BrandLogo className="h-8" />
          </a>
          <span className="text-slate-teal text-body-sm">Partner portal</span>
        </div>
      </header>

      <main className="max-w-page mx-auto px-4 py-8 sm:px-8 lg:py-12">
        <div className="grid min-w-0 items-center gap-8 lg:grid-cols-2 lg:gap-16">
          <section aria-labelledby="partner-welcome-title" className="min-w-0">
            <p className="text-ember text-body-sm mb-4 font-semibold">A partner in patient care</p>
            <h1
              id="partner-welcome-title"
              className="text-h2 sm:text-h1 max-w-120 text-balance font-serif"
            >
              Help patients take the next step.
            </h1>
            <p className="text-slate-teal text-body mt-5 max-w-lg leading-relaxed">
              Connect Medicare patients with prescription cost support. Keep referrals and follow-up
              in one place.
            </p>
            <div className="border-haze mt-7 hidden items-center gap-4 border-t pt-6 lg:flex">
              <p className="text-slate-teal text-body-sm max-w-xs leading-relaxed">
                Built for pharmacies, providers and brokers.
                <span className="text-midnight block font-medium">
                  Support, one step at a time.
                </span>
              </p>
            </div>
          </section>

          <section
            id="partner-sign-in"
            aria-labelledby="partner-sign-in-title"
            tabIndex={-1}
            className="border-haze bg-mist min-w-0 scroll-mt-4 rounded-3xl border p-5 sm:p-8"
          >
            <h2 id="partner-sign-in-title" className="text-h3 font-serif">
              {title}
            </h2>
            <p className="text-slate-teal text-body-sm mt-2 leading-relaxed">
              Use your work email and DearBill password.
            </p>
            <div className="mt-6">{children}</div>
            {footer && <div className="text-slate-teal text-body-sm mt-4">{footer}</div>}
            <p className="border-haze text-slate-teal text-body-sm mt-4 border-t pt-4 leading-relaxed">
              New to DearBill?{" "}
              <a
                href="https://dearbill.com/contact"
                className="text-primary-text inline-flex min-h-11 items-center font-semibold underline underline-offset-4"
              >
                Ask about becoming a partner
              </a>
            </p>
          </section>
        </div>

        <section
          aria-label="How DearBill supports your team"
          className="border-haze mt-10 border-t pt-8"
        >
          <ol className="grid gap-6 md:grid-cols-3 md:gap-8">
            {STEPS.map((step, index) => (
              <li key={step.title} className="flex min-w-0 flex-col items-start gap-3 sm:flex-row">
                <span
                  aria-hidden="true"
                  className="bg-cream text-ember text-body-sm flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-semibold"
                >
                  {index + 1}
                </span>
                <div className="min-w-0 break-words">
                  <h2 className="text-body font-semibold leading-relaxed">{step.title}</h2>
                  <p className="text-slate-teal text-body-sm mt-1 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </main>

      <footer className="border-haze border-t">
        <div className="max-w-page text-slate-teal text-body-sm mx-auto flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-4 sm:px-8">
          <p>Here for the people you care for.</p>
          <nav aria-label="About DearBill" className="flex flex-wrap gap-x-5">
            <a
              href="https://dearbill.com/"
              className="inline-flex min-h-11 items-center underline underline-offset-4"
            >
              About DearBill
            </a>
            <a
              href="https://dearbill.com/privacy-policy"
              className="inline-flex min-h-11 items-center underline underline-offset-4"
            >
              Privacy
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export function PartnerLoginLanding(): ReactNode {
  return <Login layout={PartnerLoginLayout} />;
}
